// Shared navigation model for the Home v2 shell.
// Both the desktop Sidebar and the mobile BottomTabBar render from these
// definitions so the two navigations never drift. Routes point at the
// existing web routes (transfer/receive/shield/swap/...) — real flow, no stubs.
import { SvgProps } from 'react-native-svg'

import ActivityIcon from '@common/assets/svg/ActivityIcon'
import BridgeIcon from '@common/assets/svg/BridgeIcon'
import DAppsIcon from '@common/assets/svg/DAppsIcon'
import GasTankIcon from '@common/assets/svg/GasTankIcon'
import GlobeIcon from '@common/assets/svg/GlobeIcon'
import ReceiveIcon from '@common/assets/svg/ReceiveIcon'
import SendIcon from '@common/assets/svg/SendIcon'
import SettingsIcon from '@common/assets/svg/SettingsIcon'
import ShieldFilledIcon from '@common/assets/svg/ShieldFilledIcon'
import SwapAndBridgeIcon from '@common/assets/svg/SwapAndBridgeIcon'
import WalletFilledIcon from '@common/assets/svg/WalletFilledIcon'
import { WEB_ROUTES } from '@common/modules/router/constants/common'
import { FEATURES } from '@web/constants/features'

export type NavKey =
  | 'home'
  | 'send'
  | 'swap'
  | 'bridge'
  | 'receive'
  | 'gasTank'
  | 'shield'
  | 'unshield'
  | 'apps'
  | 'walletconnect'
  | 'activity'
  | 'settings'

export type NavItem = {
  key: NavKey
  label: string
  icon: React.FC<SvgProps>
  /** Target route (omitted for the home tab itself / handled inline). */
  route?: string
  /**
   * Only surface this entry in PUBLIC mode (Swap, Bridge, Gas Tank). These flows
   * spend PUBLIC on-chain funds, so listing them while the dashboard shows the
   * shielded balance would read as "swap/bridge my private funds" (Trello #505).
   */
  publicOnly?: boolean
  /**
   * This entry drives a private-wallet (RAILGUN) flow — shield / unshield / the
   * private send. Disabled (with an explanatory tooltip) when the selected
   * account can't have a private wallet, e.g. it was imported from a raw private
   * key. See isPrivateModeBlocked / usePrivateModeReason.
   */
  privateAction?: boolean
  /** Show a "New" badge next to the label (sidebar, expanded only). */
  badge?: boolean
}

export type NavGroup = {
  label: string
  items: NavItem[]
}

/**
 * Two entries, not one: same-chain swaps go to `/swap` (ZKX Trade, plus
 * LibertySwap on PulseChain) and cross-chain to `/bridge`. Both the sidebar and
 * the FAB menu spread this same constant so they cannot drift apart, and both
 * appear or disappear together with `FEATURES.mobileWebSwapBridgeEnabled`.
 * Public-only: hidden in PRIVATE mode, matching the dashboard shortcuts in
 * `@common/.../Routes` (Trello #505).
 */
export const SPLIT_SWAP_NAV_ITEMS: NavItem[] = [
  { key: 'swap', label: 'Swap', icon: SwapAndBridgeIcon, route: WEB_ROUTES.swap, publicOnly: true },
  { key: 'bridge', label: 'Bridge', icon: BridgeIcon, route: WEB_ROUTES.bridge, publicOnly: true }
]

// Sidebar groups (desktop). Mirrors the design's Wallet / Privacy / More sections.
export const SIDEBAR_GROUPS: NavGroup[] = [
  {
    label: 'Wallet',
    items: [
      { key: 'home', label: 'Home', icon: WalletFilledIcon, route: WEB_ROUTES.dashboard },
      {
        key: 'send',
        label: 'Send',
        icon: SendIcon,
        route: WEB_ROUTES.transfer,
        privateAction: true
      },
      // Public Swap & Bridge, re-enabled in the production mobile-web build
      // (ZKX product decision, 2026-07-07). Private (RAILGUN) swap stays
      // dev-only — see `mobileWebPrivateSwapEnabled` in features.ts.
      ...(FEATURES.mobileWebSwapBridgeEnabled
        ? SPLIT_SWAP_NAV_ITEMS.map((item) =>
            item.key === 'swap' ? { ...item, badge: true } : item
          )
        : []),
      { key: 'receive', label: 'Receive', icon: ReceiveIcon, route: WEB_ROUTES.receive },
      // Gas Tank top-up. Public-only — the gas tank pays public-tx gas and has
      // no private-mode counterpart (mirrors GasTankPill on the balance card).
      ...(FEATURES.gasTankUiEnabled
        ? [
            {
              key: 'gasTank' as const,
              label: 'Gas Tank',
              icon: GasTankIcon,
              route: WEB_ROUTES.topUpGasTank,
              publicOnly: true
            }
          ]
        : [])
    ]
  },
  {
    label: 'Privacy',
    items: [
      {
        key: 'shield',
        label: 'Shield',
        icon: ShieldFilledIcon,
        route: WEB_ROUTES.shield,
        privateAction: true
      },
      {
        key: 'unshield',
        label: 'Unshield',
        icon: GlobeIcon,
        route: WEB_ROUTES.unshield,
        privateAction: true
      }
    ]
  },
  {
    label: 'More',
    items: [
      // Apps (dApp catalog), re-enabled in the production sidebar (ZKX
      // product decision, 2026-07-07). WalletConnect removed from the
      // sidebar entirely.
      ...(FEATURES.mobileWebDappsEnabled
        ? [{ key: 'apps' as const, label: 'Apps', icon: DAppsIcon, route: WEB_ROUTES.apps }]
        : []),
      { key: 'activity', label: 'Activity', icon: ActivityIcon, route: WEB_ROUTES.transactions },
      { key: 'settings', label: 'Settings', icon: SettingsIcon, route: WEB_ROUTES.generalSettings }
    ]
  }
]

// Bottom tab bar (mobile) — four destinations flanking a center FAB:
// [Home, Activity] · (+) · [Receive|Apps, Setting]. The FAB opens FAB_ACTIONS.
// Apps (dApp) re-enabled in prod (ZKX product decision, 2026-07-07) → takes
// the third slot instead of Receive whenever `mobileWebDappsEnabled` is on.
export const BOTTOM_TABS: NavItem[] = [
  { key: 'home', label: 'Home', icon: WalletFilledIcon, route: WEB_ROUTES.dashboard },
  { key: 'activity', label: 'Activity', icon: ActivityIcon, route: WEB_ROUTES.transactions },
  FEATURES.mobileWebDappsEnabled
    ? { key: 'apps', label: 'Apps', icon: DAppsIcon, route: WEB_ROUTES.apps }
    : { key: 'receive', label: 'Receive', icon: ReceiveIcon, route: WEB_ROUTES.receive },
  // Mobile lands on the settings menu grid; desktop sidebar opens General directly.
  { key: 'settings', label: 'Setting', icon: SettingsIcon, route: WEB_ROUTES.settingsMenu }
]

// Center FAB action menu (mobile) — the privacy/transfer quick actions that pop
// up above the tab bar when the + button is pressed. Order = top → bottom.
export const FAB_ACTIONS: NavItem[] = [
  // WalletConnect removed from the FAB menu entirely (ZKX product decision).
  {
    key: 'unshield',
    label: 'Unshield',
    icon: GlobeIcon,
    route: WEB_ROUTES.unshield,
    privateAction: true
  },
  {
    key: 'shield',
    label: 'Shield',
    icon: ShieldFilledIcon,
    route: WEB_ROUTES.shield,
    privateAction: true
  },
  { key: 'receive', label: 'Receive', icon: ReceiveIcon, route: WEB_ROUTES.receive },
  // Public Swap & Bridge, re-enabled in the production FAB menu (ZKX product
  // decision, 2026-07-07).
  ...(FEATURES.mobileWebSwapBridgeEnabled ? SPLIT_SWAP_NAV_ITEMS : []),
  // Gas Tank top-up — public-only, same gating as the sidebar entry.
  ...(FEATURES.gasTankUiEnabled
    ? [
        {
          key: 'gasTank' as const,
          label: 'Gas Tank',
          icon: GasTankIcon,
          route: WEB_ROUTES.topUpGasTank,
          publicOnly: true
        }
      ]
    : []),
  { key: 'send', label: 'Send', icon: SendIcon, route: WEB_ROUTES.transfer, privateAction: true }
]

// Round quick-action row under the balance card (mobile tier). Public mode shows
// Send · Swap · Bridge · Shield · Receive; private mode shows Send · Unshield ·
// Receive. Swap/Bridge follow the same feature flag as the sidebar and FAB.
export const QUICK_ACTIONS: Record<'public' | 'private', NavItem[]> = {
  public: [
    { key: 'send', label: 'Send', icon: SendIcon, route: WEB_ROUTES.transfer },
    ...(FEATURES.mobileWebSwapBridgeEnabled ? SPLIT_SWAP_NAV_ITEMS : []),
    {
      key: 'shield',
      label: 'Shield',
      icon: ShieldFilledIcon,
      route: WEB_ROUTES.shield,
      privateAction: true
    },
    { key: 'receive', label: 'Receive', icon: ReceiveIcon, route: WEB_ROUTES.receive }
  ],
  private: [
    {
      key: 'send',
      label: 'Send',
      icon: SendIcon,
      route: WEB_ROUTES.transfer,
      privateAction: true
    },
    {
      key: 'unshield',
      label: 'Unshield',
      icon: GlobeIcon,
      route: WEB_ROUTES.unshield,
      privateAction: true
    },
    { key: 'receive', label: 'Receive', icon: ReceiveIcon, route: WEB_ROUTES.receive }
  ]
}
